import { SupportMessage } from '@/components/ui/SupportMessage';
import { GoldenEnrolmentCard } from './GoldenEnrolmentCard';
import { useState } from 'react';
import { Loader2, ExternalLink, Link2, Mail, RefreshCw } from 'lucide-react';
import {
  createAgent, updateAgent, startAgentConnect, resendAgentInvite, copyAgentInviteLink,
  getDisplayErrorMessage, deriveInviteStatus,
  type AgentRow,
} from '@/lib/api';
import {
  EMPTY_AGENT_PAYOUT_CHOICE, agentApiTakesCountry, agentConnectStartBody, agentPayoutAccountCountryLabel,
  agentStartCountryProblem, agentStartCountryStopMessage, canStartAgentConnect,
  type AgentPayoutChoice,
} from '@/lib/agentPayoutCountry';
import { Modal } from '@/components/ui/Modal';
import { useInviteLinkDialog } from '@/components/ui/InviteLinkDialog';
import { INVITE_LINK_COPY_EN, partnerInviteOpen } from '@/lib/inviteLink';
import { AgentPayoutCountryField } from '@/components/sp/AgentPayoutCountryField';
import formStyles from '@/components/ui/Form.module.css';
import styles from './AgentsListPage.module.css';
import { Switch } from '@/components/ui/Switch';

/**
 * Create / edit form for an Agent (Phase 7a of the agent-commission
 * ticket). Single component handles both modes via the `mode` +
 * `agent` props pair , mirror of RfidCardFormModal's pattern.
 *
 * Create fields: name, email, phone, default_commission_bps, notes.
 *   - name + email required (also enforced server-side via JSON-schema)
 *   - email format validated by the browser's input type=email
 *     (server re-validates + checks uniqueness)
 *   - phone E.164 pattern (server schema regex backs this up)
 *   - default_commission_bps as percent (0 to 50%, bps stored on the
 *     wire , same boundary conversion as Phase 7b's Network Origin
 *     commission input)
 *
 * Edit mode additionally surfaces:
 *   - active toggle (PATCH { active: false } is the soft-delete path
 *     per Phase 7a discovery §10.2 , no dedicated DELETE endpoint)
 *   - Read-only: Connect status, outstanding clawback, audit metadata
 *
 * Server error handling:
 *   - 409 → "Email already in use" (the create + email-changed
 *     PATCH paths both return this shape)
 *   - 4xx VALIDATION_ERROR → surface verbatim
 *   - 5xx → generic "Failed to save"
 *   - All routed through getDisplayErrorMessage's existing branch
 *     coverage so the error UX is consistent across admin pages.
 */

interface Props {
  mode: 'create' | 'edit';
  agent?: AgentRow;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

export function AgentFormModal({ mode, agent, onClose, onSaved }: Props) {
  // Phase 7g.1 , local copy of the agent that updates after a
  // resend so the modal renders the fresh expiry and status
  // without closing. Parent reload picks up the same state on next
  // open via onSaved → reload flow.
  const [liveAgent, setLiveAgent] = useState<AgentRow | undefined>(agent);
  const [name, setName] = useState(agent?.name ?? '');
  const [contactName, setContactName] = useState(agent?.contact_name ?? '');
  const [email, setEmail] = useState(agent?.email ?? '');
  const [phone, setPhone] = useState(agent?.phone ?? '');
  const [notes, setNotes] = useState(agent?.notes ?? '');
  const [active, setActive] = useState(agent?.active ?? true);
  // The payout account country (item 6, 2026-10-03). No longer a form
  // field with a 'CA' default: it is chosen explicitly, US or Canada,
  // right where onboarding starts, and only while no account exists.
  // Agent.country was read by nothing but the Stripe create, so the
  // create and edit forms no longer carry it.
  const [payoutChoice, setPayoutChoice] = useState<AgentPayoutChoice>(EMPTY_AGENT_PAYOUT_CHOICE);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Phase 7a.1 , separate busy + error state for the Connect-start
  // button so a Stripe roundtrip failure doesn't get conflated with
  // a form-save failure. busy disables the button (and the form
  // surface around it) while the link is minted; the response URL
  // hands off to Stripe-hosted onboarding via window.location.
  const [connectBusy, setConnectBusy] = useState(false);
  const [connectErr, setConnectErr] = useState('');

  // Phase 7g.1 resend state: resend disables the button and surfaces
  // errors inline through the same getDisplayErrorMessage helper the
  // rest of the form uses. Copy invite link (29 October 2026) replaces
  // the standing link this form used to show: a fresh link, made on
  // demand and shown once in its own dialog.
  const [inviteResendBusy, setInviteResendBusy] = useState(false);
  const [inviteErr, setInviteErr] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const inviteLink = useInviteLinkDialog();

  const handleResendInvite = async (): Promise<void> => {
    if (!liveAgent || inviteResendBusy) return;
    setInviteErr('');
    setInviteNote('');
    setInviteResendBusy(true);
    try {
      const refreshed = await resendAgentInvite(liveAgent.id);
      setLiveAgent(refreshed);
      if (refreshed.invite_email?.ok) setInviteNote(`Invite emailed to ${refreshed.invite_email.to ?? refreshed.email}. The old link no longer works.`);
      else setInviteErr('A new link is ready, but the email did not go. Use Copy invite link to send it yourself.');
    } catch (err) {
      setInviteErr(await getDisplayErrorMessage(err, 'Failed to resend invite'));
    } finally {
      setInviteResendBusy(false);
    }
  };

  const handleStartConnect = async (): Promise<void> => {
    if (!agent) return;
    const hasAccount = !!agent.stripe_connect_account_id;
    if (!canStartAgentConnect(hasAccount, payoutChoice, agentApiTakesCountry(agent))) return;
    setConnectErr('');
    setConnectBusy(true);
    try {
      // The country only for a new account; a resume sends nothing.
      const body = agentConnectStartBody(hasAccount, payoutChoice);
      const { url, country } = await startAgentConnect(agent.id, body);
      // A start that sent a country goes on to Stripe only when the
      // account is in that country (a lost race, a stale screen or an
      // older Api can point at one in the other country).
      const problem = agentStartCountryProblem(body?.country, country);
      if (body && problem) {
        setConnectBusy(false);
        setConnectErr(agentStartCountryStopMessage('partner', body.country, problem));
        return;
      }
      // Hard navigation , Stripe-hosted onboarding is a top-level
      // redirect, not an iframe / popup. Mirrors how the tenant
      // PayoutsSection on SettingsPage handles its equivalent.
      window.location.href = url;
    } catch (err) {
      setConnectBusy(false);
      setConnectErr(await getDisplayErrorMessage(err, 'Failed to start onboarding'));
    }
  };

  // Client-side E.164 validation. Stricter than the server's regex
  // (`/^\+[1-9]\d{1,14}$/` on Agent.phone) , we require 7+ digits
  // after the country code so a "14155552671" (missing the
  // leading +) gets the inline error instead of round-tripping to a
  // generic 500. The server's looser regex stays as the backstop;
  // asymmetric strictness is intentional , the client guards against
  // the common typo while the server stays permissive for weird-but-
  // valid international shapes a human reviewer can sign off on.
  // Empty phone is valid (optional field).
  const trimmedPhone = phone.trim();
  const phoneError = trimmedPhone && !/^\+[1-9]\d{6,14}$/.test(trimmedPhone)
    ? 'Phone must be in E.164 format (e.g. +14155552671)'
    : '';

  const handleSubmit = async (): Promise<void> => {
    setSubmitting(true);
    setError('');
    try {
      if (mode === 'create') {
        if (!name.trim()) throw new Error('Name is required');
        if (!email.trim()) throw new Error('Email is required');
        await createAgent({
          name: name.trim(),
          contact_name: contactName.trim() || null,
          email: email.trim(),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        });
      } else if (agent) {
        // Edit mode: send the full editable shape. Server is
        // idempotent on no-change fields (the schema's $set with
        // the same value is a Mongo no-op for that field). Country
        // is never sent: it is chosen at Connect start and recorded
        // by that route.
        await updateAgent(agent.id, {
          name: name.trim(),
          contact_name: contactName.trim() || null,
          email: email.trim(),
          phone: phone.trim() || null,
          notes: notes.trim() || null,
          active,
        });
      }
      await onSaved();
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Failed to save'));
    } finally {
      setSubmitting(false);
    }
  };

  const connectReady = !!agent?.stripe_connect_account_id;
  const takesCountry = agentApiTakesCountry(agent);
  const payoutCountryLabel = agent ? agentPayoutAccountCountryLabel(agent) : null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={mode === 'create' ? 'Add Sales Partner' : 'Edit Sales Partner'}
      size="md"
    >
      <form
        className={styles.modalForm}
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        {error && <div className={formStyles.formError}>{error}</div>}

        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>Name</label>
          <input
            className={formStyles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Sales Co"
            required
            maxLength={200}
          />
        </div>

        <div className={formStyles.field}>
          <label className={formStyles.label} htmlFor="partner-contact-name">Contact name</label>
          <input id="partner-contact-name" className={formStyles.input} value={contactName}
            onChange={(e) => setContactName(e.target.value)} maxLength={200} autoComplete="name" />
        </div>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>Email</label>
          <input
            className={formStyles.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="partner@example.com"
            required
            maxLength={254}
          />
          <p className={styles.hint}>
            Must be unique across all agents. Used for Connect
            onboarding when that flow ships.
          </p>
        </div>

        <div className={formStyles.field}>
          <label className={formStyles.label}>Phone</label>
          <input
            className={formStyles.input}
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+14155552671"
            maxLength={16}
          />
          {phoneError && (
            <div className={formStyles.formError}>{phoneError}</div>
          )}
          <p className={styles.hint}>
            Optional. Include the <code>+</code> and country code.
            Example: <code>+1</code> for US/Canada, <code>+44</code>{' '}
            for UK.
          </p>
        </div>

        {mode === 'edit' && agent && <GoldenEnrolmentCard agentId={agent.id} />}

        <div className={formStyles.field}>
          <label className={formStyles.label}>Notes</label>
          <textarea
            className={formStyles.input}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes about this Sales Partner: deal terms, contact preferences, etc."
            maxLength={2000}
            rows={4}
          />
        </div>

        {mode === 'edit' && agent && (
          <>
            <div className={formStyles.field}>
              <label className={formStyles.label}>Status</label>
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                label={<>
                  <strong>Sales Partner is active</strong>
                  <span className={styles.toggleHint}>
                    {' '}Switch off to deactivate. Existing payout
                    history stays intact; new commissions skip on
                    chargers still pointing at this Sales Partner.
                  </span>
                </>}
              />
            </div>

            {/* Phase 7g.1 , Sales Partner invite section.
                Visible whenever the SP hasn't yet accepted (until
                Phase 7g.3's accept endpoint flips
                invite_accepted_at, this is always shown in edit
                mode). Three states branch on the derived status:
                  - pending: copy URL + regenerate
                  - expired: same UI, regenerate styled prominent
                  - accepted: section hidden entirely
                Resend invite emails a fresh link (20 October 2026);
                approval in Referral applications sends the first one.
                Copy invite link (29 October 2026) makes a fresh link to
                send by hand, within the current expiry, shown once; it
                follows the same rule as Resend (lib/inviteLink.ts). */}
            {liveAgent && !liveAgent.invite_accepted_at && (() => {
              const status = deriveInviteStatus(liveAgent);
              const expiresAt = liveAgent.invite_token_expires_at
                ? new Date(liveAgent.invite_token_expires_at)
                : null;
              const expiresLabel = expiresAt
                ? expiresAt.toLocaleDateString(undefined, {
                  year: 'numeric', month: 'short', day: 'numeric',
                })
                : null;
              const canResend = partnerInviteOpen(liveAgent);
              return (
                <div className={styles.readonlyBlock}>
                  <div className={styles.readonlyRow}>
                    <span className={styles.readonlyLabel}>Invite status</span>
                    <span className={styles.readonlyValue}>
                      {status === 'expired' ? (
                        <span className={styles.chipWarn}>Expired</span>
                      ) : (
                        <span className={styles.chipInfo}>
                          <Mail size={11} /> Pending
                        </span>
                      )}
                      {expiresLabel && (
                        <span className={styles.toggleHint}>
                          {' '}, {status === 'expired'
                            ? `expired ${expiresLabel}`
                            : `expires ${expiresLabel}`}
                        </span>
                      )}
                    </span>
                  </div>
                  {inviteNote && <p className={styles.hint} role="status">{inviteNote}</p>}
                  {inviteErr && (
                    <div className={formStyles.formError}>{inviteErr}</div>
                  )}
                  <div className={styles.connectActionRow}>
                    <button
                      type="button"
                      className={formStyles.btnSecondary}
                      onClick={() => void handleResendInvite()}
                      disabled={inviteResendBusy || !canResend}
                      title={!canResend
                        ? 'Reactivate this Sales Partner before resending the invite.'
                        : undefined}
                    >
                      {inviteResendBusy ? (
                        <><Loader2 size={12} className={styles.spin} /> Sending…</>
                      ) : (
                        <><RefreshCw size={12} /> Resend invite</>
                      )}
                    </button>
                    <button
                      type="button"
                      className={formStyles.btnSecondary}
                      onClick={() => void inviteLink.open(
                        { email: liveAgent.email, name: liveAgent.contact_name || liveAgent.name },
                        () => copyAgentInviteLink(liveAgent.id),
                      )}
                      disabled={inviteResendBusy || !canResend || inviteLink.busyFor !== null}
                      title={!canResend
                        ? 'Reactivate this Sales Partner before copying the invite link.'
                        : undefined}
                    >
                      <Link2 size={12} /> {INVITE_LINK_COPY_EN.action}
                    </button>
                  </div>
                  <p className={styles.hint}>
                    {!canResend
                      ? 'Reactivate this Sales Partner before resending the invite.'
                      : status === 'expired'
                        ? 'The current link has expired. Resend the invite to start a fresh 21-day link, then copy it if you are sending it yourself.'
                        : 'Resend invite emails a fresh 21-day link. Copy invite link gives you a fresh link to send from your own email, within the current expiry.'}
                  </p>
                </div>
              );
            })()}

            {/* Read-only Connect + clawback state */}
            <div className={styles.readonlyBlock}>
              <div className={styles.readonlyRow}>
                <span className={styles.readonlyLabel}>Stripe Connect</span>
                <span className={styles.readonlyValue}>
                  {connectReady
                    ? `✓ Ready (${agent.stripe_connect_account_id})`
                    : '⚠ Onboarding incomplete , transfers queue as pending_stripe_setup'}
                </span>
              </div>
              {payoutCountryLabel && (
                <div className={styles.readonlyRow}>
                  <span className={styles.readonlyLabel}>Payout account country</span>
                  <span className={styles.readonlyValue}>{payoutCountryLabel}</span>
                </div>
              )}
              {/* Item 6 (2026-10-03): a first-time setup asks for the
                  bank account country, US or Canada, with nothing
                  preselected and a confirmation; a resume asks for
                  nothing (Stripe cannot change the country). */}
              {!connectReady && (
                <AgentPayoutCountryField
                  idPrefix={`agent-${agent.id}`}
                  subject="partner"
                  value={payoutChoice}
                  onChange={setPayoutChoice}
                  updating={!takesCountry}
                  disabled={connectBusy || submitting}
                />
              )}
              {/* Phase 7a.1 , onboarding link button. Two labels
                  depending on state: first-time setup vs resuming
                  an in-progress Connect account. The /start route
                  is idempotent across both cases. */}
              <div className={styles.connectActionRow}>
                {connectErr && (
                  <div className={formStyles.formError}><SupportMessage text={connectErr} /></div>
                )}
                <button
                  type="button"
                  className={formStyles.btnSecondary}
                  onClick={() => void handleStartConnect()}
                  disabled={connectBusy || submitting || !canStartAgentConnect(connectReady, payoutChoice, takesCountry)}
                >
                  {connectBusy ? (
                    <><Loader2 size={12} className={styles.spin} /> Opening Stripe…</>
                  ) : (
                    <>
                      <ExternalLink size={12} />
                      {' '}
                      {connectReady
                        ? 'Refresh onboarding link'
                        : 'Set up Stripe Connect'}
                    </>
                  )}
                </button>
              </div>
              <div className={styles.readonlyRow}>
                <span className={styles.readonlyLabel}>Outstanding clawback</span>
                <span className={styles.readonlyValue}>
                  ${(Math.max(0, agent.outstanding_clawback_cents) / 100).toFixed(2)}
                  {agent.outstanding_clawback_cents > 0 && (
                    <span className={styles.toggleHint}>
                      {' '}, deducted from future commissions until cleared
                    </span>
                  )}
                </span>
              </div>
              <div className={styles.readonlyRow}>
                <span className={styles.readonlyLabel}>Created</span>
                <span className={styles.readonlyValue}>
                  {new Date(agent.created_at).toLocaleString()}
                  {agent.created_by_user_id && (
                    <> by user &hellip;{agent.created_by_user_id.slice(-8)}</>
                  )}
                </span>
              </div>
              {agent.updated_at !== agent.created_at && (
                <div className={styles.readonlyRow}>
                  <span className={styles.readonlyLabel}>Last updated</span>
                  <span className={styles.readonlyValue}>
                    {new Date(agent.updated_at).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        <div className={styles.modalActions}>
          <button
            type="button"
            className={formStyles.btnSecondary}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={formStyles.btnPrimary}
            disabled={submitting || !name.trim() || !email.trim() || !!phoneError}
          >
            {submitting
              ? <><Loader2 size={12} className={styles.spin} /> Saving…</>
              : mode === 'create' ? 'Create Sales Partner' : 'Save changes'}
          </button>
        </div>
      </form>
      {/* Copy invite link's dialog: a portal above this one; its clicks stop at this modal. */}
      {inviteLink.dialog}
    </Modal>
  );
}
